/**
 * World Cup country flag: loads from the assets CDN at runtime (not bundled).
 * Uses memory + disk caching and list recycling hints to avoid repeat network
 * fetches when scrolling virtualized lists.
 * When the URL is missing or the image errors, optional `kitFallback` shows a SoccerKit.
 */

import React, { useMemo, memo, useState, useEffect } from 'react';
import { View, Platform } from 'react-native';
import { Image } from 'expo-image';
import { useTheme } from '../../../src/theme/useTheme';
import type { TeamKitConfig } from '../../../constants/teamKitColors';
import { getFlagCdnUrl } from '../../../src/worldcup/helpers/flagCdn.helper';
import { WEB_IMAGE_ALT_DECORATIVE } from '../../../src/shared/helpers/imageWebAlt.helper';
import { SoccerKit } from '../../ui/SoccerKit';
import { getCountryFlagStyles } from './CountryFlag.styles';

export interface CountryFlagProps {
  countryCode: string;
  /** Shown when the flag URL is invalid or the image fails to load (e.g. national team kit from name map). */
  kitFallback?: TeamKitConfig;
  /** Accessibility label for the flag image */
  accessibilityLabel?: string;
  testID?: string;
  /** Smaller flag for league headers / dense rows; mini for compact match info. */
  size?: 'default' | 'compact' | 'mini';
}

function kitSizePx(flagSize: 'default' | 'compact' | 'mini'): number {
  if (flagSize === 'mini') return 22;
  if (flagSize === 'compact') return 28;
  return 40;
}

const CountryFlagInner: React.FC<CountryFlagProps> = ({
  countryCode,
  kitFallback,
  accessibilityLabel,
  testID,
  size = 'default',
}) => {
  const theme = useTheme();
  const styles = useMemo(() => getCountryFlagStyles(theme, size), [theme, size]);
  const uri = useMemo(() => getFlagCdnUrl(countryCode), [countryCode]);
  const source = useMemo(
    () => (uri != null ? { uri, cacheKey: uri } : null),
    [uri],
  );

  const [loadFailed, setLoadFailed] = useState(false);
  useEffect(() => {
    setLoadFailed(false);
  }, [uri]);

  const kitPx = useMemo(() => kitSizePx(size), [size]);

  const showKitInstead =
    kitFallback != null && (!uri || source == null || loadFailed);

  if (showKitInstead) {
    const k = kitFallback;
    return (
      <View
        style={styles.wrap}
        testID={testID}
        accessibilityRole="image"
        accessibilityLabel={accessibilityLabel}
      >
        <SoccerKit
          primary={k.primary}
          secondary={k.secondary}
          type={k.type}
          size={kitPx}
        />
      </View>
    );
  }

  if (!uri || source == null || loadFailed) {
    return <View style={styles.placeholder} testID={testID} accessibilityRole="image" accessibilityLabel={accessibilityLabel} />;
  }

  return (
    <View style={styles.wrap} testID={testID}>
      <Image
        source={source}
        style={styles.image}
        contentFit="cover"
        cachePolicy="memory-disk"
        recyclingKey={Platform.OS === 'web' ? undefined : uri}
        onError={() => setLoadFailed(true)}
        accessibilityLabel={accessibilityLabel ?? WEB_IMAGE_ALT_DECORATIVE}
        alt={WEB_IMAGE_ALT_DECORATIVE}
      />
    </View>
  );
};

export const CountryFlag = memo(CountryFlagInner);
